import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraBadge } from '../ensigns/badge';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraText } from '../ensigns/typography';
import { SpecraOrgSeatCard } from '../lieutenants/org-seat-card';
function buildTree(nodes) {
    const ids = new Set(nodes.map((n)=>n.id));
    const roots = [];
    const children = new Map();
    for (const n of nodes){
        if (n.parentId === null || !ids.has(n.parentId) || n.parentId === n.id) roots.push(n);
        else {
            const list = children.get(n.parentId) ?? [];
            list.push(n);
            children.set(n.parentId, list);
        }
    }
    return {
        roots,
        children
    };
}
/** Tidy left-to-right tree (people.js:74-83): leaves take the next row; a parent's row is the midpoint of its first and last child rows. Roots are nodes with parentId null or an unknown parent. */ export function layoutOrgTree(nodes) {
    const { roots, children } = buildTree(nodes);
    const positions = new Map();
    let next = 0;
    let maxDepth = -1;
    const place = (node, depth)=>{
        if (positions.has(node.id)) return; // cycle guard
        positions.set(node.id, {
            depth,
            row: next
        });
        maxDepth = Math.max(maxDepth, depth);
        const kids = (children.get(node.id) ?? []).filter((k)=>!positions.has(k.id));
        if (kids.length === 0) {
            next += 1;
            return;
        }
        for (const k of kids)place(k, depth + 1);
        const rows = kids.map((k)=>positions.get(k.id)?.row ?? 0);
        positions.set(node.id, {
            depth,
            row: (Math.min(...rows) + Math.max(...rows)) / 2
        });
    };
    for (const r of roots)place(r, 0);
    return {
        positions,
        rows: next,
        depth: maxDepth + 1
    };
}
const COL = 'calc(var(--component-org-card-width) + var(--component-org-column-gap))';
const ROW = 'var(--component-org-row-height)';
const HALF_CARD = 'var(--component-org-card-height) / 2';
/** A tidy org tree of seat cards with elbow connectors and departure tags. */ export function SpecraOrgChart({ nodes, selectedId, onNodeSelect, label, emptyMessage, getNodeTestId, className, ...props }) {
    const layout = React.useMemo(()=>layoutOrgTree(nodes), [
        nodes
    ]);
    const tree = React.useMemo(()=>buildTree(nodes), [
        nodes
    ]);
    if (nodes.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: className,
            ...props,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "body-sm",
                tone: "muted",
                children: emptyMessage
            })
        });
    }
    const byId = new Map(nodes.map((n)=>[
            n.id,
            n
        ]));
    const connectors = [];
    for (const n of nodes){
        const p = n.parentId === null ? undefined : byId.get(n.parentId);
        const at = layout.positions.get(n.id);
        const pat = p ? layout.positions.get(p.id) : undefined;
        if (!p || !at || !pat || p.id === n.id) continue;
        const x1 = `(${pat.depth} * ${COL} + var(--component-org-card-width))`;
        const mx = `(${x1} + var(--component-org-connector-stub))`;
        const y1 = `(${pat.row} * ${ROW} + ${HALF_CARD})`;
        const y2 = `(${at.row} * ${ROW} + ${HALF_CARD})`;
        const top = `(${Math.min(pat.row, at.row)} * ${ROW} + ${HALF_CARD})`;
        connectors.push(/*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "absolute h-0 border-t border-[color:var(--border-dashed)]",
                    style: {
                        left: `calc${x1}`,
                        top: `calc${y1}`,
                        width: 'var(--component-org-connector-stub)'
                    }
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "absolute w-0 border-l border-[color:var(--border-dashed)]",
                    style: {
                        left: `calc${mx}`,
                        top: `calc${top}`,
                        height: `calc(${Math.abs(at.row - pat.row)} * ${ROW})`
                    }
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "absolute h-0 border-t border-[color:var(--border-dashed)]",
                    style: {
                        left: `calc${mx}`,
                        top: `calc${y2}`,
                        width: `calc(${at.depth} * ${COL} - ${mx})`
                    }
                })
            ]
        }, n.id));
    }
    const renderNode = (node, seen)=>{
        const at = layout.positions.get(node.id);
        if (!at || seen.has(node.id)) return null;
        seen.add(node.id);
        const kids = tree.children.get(node.id) ?? [];
        const selectable = node.selectable ?? Boolean(node.seat.holder);
        return /*#__PURE__*/ _jsxs("li", {
            "data-item-id": node.id,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "absolute",
                    style: {
                        left: `calc(${at.depth} * ${COL})`,
                        top: `calc(${at.row} * ${ROW})`
                    },
                    children: [
                        /*#__PURE__*/ _jsx(SpecraOrgSeatCard, {
                            ...node.seat,
                            "data-testid": getNodeTestId?.(node) ?? node.seat['data-testid'],
                            onClick: selectable && onNodeSelect ? ()=>onNodeSelect(node.id) : undefined,
                            selected: selectedId === node.id
                        }),
                        node.departure ? /*#__PURE__*/ _jsx("div", {
                            className: "ml-[var(--space-1-5)] mt-[var(--space-1)]",
                            children: node.departure.onActivate ? /*#__PURE__*/ _jsx("button", {
                                className: cn('rounded-[var(--radius-sm)]', specraFocusRing),
                                onClick: node.departure.onActivate,
                                type: "button",
                                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "danger",
                                    children: node.departure.label
                                })
                            }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "danger",
                                children: node.departure.label
                            })
                        }) : null
                    ]
                }),
                kids.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                    className: "m-0 list-none p-0",
                    children: kids.map((k)=>renderNode(k, seen))
                }) : null
            ]
        }, node.id);
    };
    const seen = new Set();
    return /*#__PURE__*/ _jsx("div", {
        className: cn('overflow-x-auto', className),
        ...props,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            style: {
                width: `calc(${layout.depth} * ${COL})`,
                height: `calc(${layout.rows} * ${ROW})`
            },
            children: [
                /*#__PURE__*/ _jsx("div", {
                    "aria-hidden": "true",
                    children: connectors
                }),
                /*#__PURE__*/ _jsx("ul", {
                    "aria-label": label,
                    className: "m-0 list-none p-0",
                    children: tree.roots.map((r)=>renderNode(r, seen))
                })
            ]
        })
    });
}
